import type { Agent } from './types'

/**
 * Tab membership rules, ported from the old dashboard's own definitions so the
 * counts match what Renee is used to seeing. These are not re-inventions —
 * `currentlyCapped` and `eliteRecent` below are the same logic as the old
 * `Agent Care Dashboard.html`, translated to the new column names.
 */

const asDate = (v: string | null): Date | null => {
  if (!v) return null
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Capped means capped *in the current cap cycle*. A cap date on its own is not
 * enough — the cycle resets annually, so a cap earned before the cycle that
 * ends at cap_reset_date no longer counts. Where there is no reset date, any
 * cap date counts, which is how the old dashboard behaved.
 *
 * **`reset` is only ever a live, upcoming boundary while Metabase keeps
 * reporting it.** `sync-metabase.ts`'s `planBackfill` only ever writes
 * `cap_reset_date` for an agent CURRENTLY on Metabase's capped card — the
 * moment an agent's cap year turns over without a new cap, Metabase simply
 * drops them from that card, and nothing ever tells our side the boundary
 * has passed. Left unchecked, `capped >= cycleStart` stays true forever once
 * it is ever true, because both dates it compares are fixed and neither one
 * moves on its own. That is exactly the bug Chris caught 2026-09-07: 10
 * agents still showing Capped weeks after Metabase had dropped them, every
 * one of them sitting on a `cap_reset_date` that had already gone by.
 *
 * So this also has to ask a second question `reset` alone can't answer: has
 * TODAY already passed the boundary Metabase last told us about? Once it
 * has, the cycle that `reset` named is over — an agent still on file with
 * that same old reset date needs Metabase to hand back a NEW one (proof they
 * capped again) before they count as capped a second time, not the memory of
 * the date that already passed.
 *
 * `today` defaults to the real clock and takes a parameter only so a test can
 * pin it — every real call site (`filters.ts`, `exporting.ts`, `badges`
 * below) calls this with one argument, same as before.
 */
export function currentlyCapped(a: Agent, today: Date = new Date()): boolean {
  const capped = asDate(a.date_became_capped)
  if (!capped) return false
  const reset = asDate(a.cap_reset_date)
  if (!reset) return true
  if (today > reset) return false
  const cycleStart = new Date(reset.getTime() - 365 * 86_400_000)
  return capped >= cycleStart
}

/** Elite within the last twelve months — the old "Elite (12 mo)" tab. */
export function eliteRecent(a: Agent): boolean {
  const elite = asDate(a.date_became_elite)
  if (!elite) return false
  return (Date.now() - elite.getTime()) / 86_400_000 <= 365
}

export const area = (a: Agent): string =>
  a.region || a.primary_market || 'Unassigned'

export const mlsPrimary = (a: Agent): string => {
  const m = a.mls as { primary?: unknown } | null
  const v = m?.primary
  return typeof v === 'string' && v.trim() ? v : 'Unassigned'
}

export type Badge = { code: 'E' | 'C' | 'L'; label: string }

export function badges(a: Agent): Badge[] {
  const out: Badge[] = []
  if (eliteRecent(a)) out.push({ code: 'E', label: 'ELITE' })
  if (currentlyCapped(a)) out.push({ code: 'C', label: 'CAP' })
  if (a.is_team_leader) out.push({ code: 'L', label: 'LEAD' })
  return out
}
